'use client';

import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { useCurrentRpcLatency } from 'src/hooks/useRpcLatency';
import RpcSelectorPopup from './RpcSelectorPopup';
import useTargetedPopover from 'src/hooks/useTargetedPopover';
import PopoverWrapper from './PopoverWrapper';
import { cj } from 'src/utils/classNames';
import useModal from 'src/components/Modals/hooks/useModal';
import { WithClassName, WithRef } from 'src/utils/UtilityTypes';
import FooterButton from 'src/components/Buttons/FooterButton';

interface CurrentRpcHealthProps extends WithClassName {
	mobile?: boolean;
}

const CurrentRpcHealth = ({ mobile, className }: CurrentRpcHealthProps) => {
	const currentRpcLatency = useCurrentRpcLatency();
	const [settings] = useCurrentSettings();
	const { show } = useModal();

	const {
		refs,
		floatingStyles,
		getReferenceProps,
		getFloatingProps,
		setIsPopoverOpen,
		isPopoverOpen,
	} = useTargetedPopover(
		{
			placement: 'top',
		},
		{
			offset: 12,
			disableAutoPlacement: true,
		}
	);

	const showNetworkSettingsModal = () => {
		show({ type: 'settings', props: { view: 'network' } });
	};

	if (mobile) {
		return (
			<RpcButton
				className={className}
				onClick={showNetworkSettingsModal}
				latency={currentRpcLatency?.avg}
				label={settings.rpcEndpoint?.label}
				mobile
			/>
		);
	}

	return (
		<>
			{isPopoverOpen && (
				<PopoverWrapper
					ref={refs.setFloating}
					style={floatingStyles}
					{...getFloatingProps()}
				>
					<RpcSelectorPopup />
				</PopoverWrapper>
			)}
			<RpcButton
				className={className}
				onClick={() => setIsPopoverOpen(!isPopoverOpen)}
				latency={currentRpcLatency?.avg}
				label={settings.rpcEndpoint?.label}
				ref={refs.setReference}
				{...getReferenceProps()}
			/>
		</>
	);
};

interface RpcButtonProps extends WithClassName {
	onClick: () => void;
	latency: number | null;
	label?: string;
	mobile?: boolean;
}

function RpcButton({
	onClick,
	latency,
	className,
	label,
	mobile,
	...rest
}: WithRef<RpcButtonProps, HTMLButtonElement>) {
	return (
		<FooterButton
			className={className}
			onClick={onClick}
			data-testid="footer-rpc-indicator"
			{...rest}
		>
			<RpcHealthBars mobile={mobile} latency={latency ?? 0} />
			<span>
				RPC: {label} {latency && `(${latency}ms)`}
			</span>
		</FooterButton>
	);
}

const RpcHealthBars = ({
	mobile,
	latency,
}: {
	latency: number;
	mobile?: boolean;
}) => {
	const barColors = (() => {
		const bgColor = mobile ? 'bg-text-default' : 'bg-text-secondary';

		if (latency < 0) {
			return [bgColor, bgColor, bgColor];
		}

		if (latency < 100) {
			return ['bg-positive-green', 'bg-positive-green', 'bg-positive-green'];
		}

		if (latency < 250) {
			return ['bg-star-yellow', 'bg-star-yellow', bgColor];
		}

		return ['bg-negative-red', bgColor, bgColor];
	})();

	return (
		<div className="flex size-md items-baseline justify-between py-2xs">
			<div className={cj('h-[40%] w-[2px] rounded-t-xs', barColors[0])} />
			<div className={cj('h-[70%] w-[2px] rounded-t-xs', barColors[1])} />
			<div className={cj('h-full w-[2px] rounded-t-xs', barColors[2])} />
		</div>
	);
};

export default CurrentRpcHealth;
